/**
 * DealLostPhaseSelectorModal - Modal para escolher fase de perda quando há múltiplas
 *
 * Quando um pipeline possui mais de uma fase do tipo `is_lost = true`, cada fase
 * representa um motivo de perda diferente. Este modal permite ao usuário escolher
 * qual delas será usada ao marcar o negócio como perdido.
 *
 * Visual alinhado ao modal premium de negócio (cards com radio + bolinha de cor).
 *
 * Versão: 1.0.0
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { XCircle, Loader2, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

// Estrutura mínima de fase de perda exigida pelo modal
export interface LostPhaseOption {
  phase_id: string;
  phase_name: string;
  phase_color: string | null;
}

interface DealLostPhaseSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Lista de fases is_lost do pipeline atual */
  lostPhases: LostPhaseOption[];
  /** Disparado quando o usuário confirma a escolha */
  onConfirm: (phaseId: string) => void;
  /** Estado de loading enquanto a mutation roda */
  isPending?: boolean;
}

/**
 * Modal premium de seleção de motivo de perda.
 */
export function DealLostPhaseSelectorModal({
  open,
  onOpenChange,
  lostPhases,
  onConfirm,
  isPending = false,
}: DealLostPhaseSelectorModalProps) {
  // Estado interno do radio
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Reseta seleção sempre que o modal reabre
  useEffect(() => {
    if (open) setSelectedId(null);
  }, [open]);

  const handleConfirm = () => {
    if (!selectedId) return;
    onConfirm(selectedId);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md gap-0 p-0 overflow-hidden">
        {/* Header com ícone vermelho rosé */}
        <DialogHeader className="px-5 pt-5 pb-3 space-y-2">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-destructive/10">
              <XCircle className="h-5 w-5 text-destructive" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-base font-semibold">
                Selecionar motivo da perda
              </DialogTitle>
              <DialogDescription className="text-xs mt-0.5">
                Escolha qual fase representa o motivo deste negócio ter sido perdido.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Lista de fases de perda como cards radio */}
        <div className="px-5 py-3 border-t">
          {lostPhases.length === 0 ? (
            <div className="text-center py-6 text-sm text-muted-foreground">
              Nenhuma fase de perda configurada neste pipeline.
            </div>
          ) : (
            <ScrollArea className="max-h-[320px] -mx-1 px-1">
              <RadioGroup
                value={selectedId ?? ''}
                onValueChange={setSelectedId}
                className="gap-2"
              >
                {lostPhases.map((phase) => {
                  const isSelected = selectedId === phase.phase_id;
                  return (
                    <Label
                      key={phase.phase_id}
                      htmlFor={`lost-${phase.phase_id}`}
                      className={cn(
                        'flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors',
                        'hover:bg-muted/50',
                        isSelected
                          ? 'border-destructive bg-destructive/5'
                          : 'border-border'
                      )}
                    >
                      <RadioGroupItem
                        id={`lost-${phase.phase_id}`}
                        value={phase.phase_id}
                        className="sr-only"
                      />
                      {/* Bolinha colorida */}
                      <div
                        className="h-3 w-3 rounded-full shrink-0 ring-1 ring-border"
                        style={{ backgroundColor: phase.phase_color ?? '#9ca3af' }}
                      />
                      <span className="flex-1 text-sm font-medium truncate">
                        {phase.phase_name}
                      </span>
                      {/* Check à direita quando selecionada */}
                      {isSelected && (
                        <Check className="h-4 w-4 text-destructive shrink-0" />
                      )}
                    </Label>
                  );
                })}
              </RadioGroup>
            </ScrollArea>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="px-5 py-3 border-t bg-muted/20 gap-2 sm:gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={!selectedId || isPending || lostPhases.length === 0}
            className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
          >
            {isPending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                <XCircle className="h-3.5 w-3.5" />
                Confirmar perda
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
